import * as XLSX from 'xlsx';

const MONTHS:any = {
  'JANVIER':1, 'FÉVRIER':2, 'FEVRIER':2, 'MARS':3, 'AVRIL':4, 'MAI':5, 'JUIN':6,
  'JUILLET':7, 'AOÛT':8, 'AOUT':8, 'SEPTEMBRE':9, 'SEPT.':9, 'OCTOBRE':10,
  'NOVEMBRE':11, 'DÉCEMBRE':12, 'DECEMBRE':12,
};

const norm = (v:any) => String(v ?? '').trim().toUpperCase();
const num = (v:any) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).replace(/\s/g,'').replace(',', '.');
  if (s.startsWith('#')) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const code = (v:any) => {
  const s = norm(v).replace(/\s+/g,' ');
  if (s.startsWith('GAZ') || s.startsWith('BUTANE')) return 'GPL';
  const map:any = {
    'SUPER 91':'SUPER', 'SUPER':'SUPER', 'PETROLE':'PETROL', 'PÉTROLE':'PETROL',
    'GASOIL':'GASOIL', 'DDO':'DDO', 'FUEL OIL':'FUELOIL', 'JET A1':'JETA1', 'TOTAL':'TOTAL',
  };
  return map[s] || s.replace(/[^A-Z0-9]/g,'');
};

const monthNo = (v:any) => MONTHS[norm(v)];
const period = (fy:string, m:number) => `${fy}${String(m).padStart(2,'0')}`;

const rowsOf = (wb:XLSX.WorkBook, sheet:string) =>
  wb.Sheets[sheet] ? XLSX.utils.sheet_to_json(wb.Sheets[sheet], {header:1, defval:null, raw:true}) as any[][] : [];

const addFact = (facts:any[], f:any) => facts.push({
  fyr:f.fyr, mno:f.mno ?? null, pdc:f.pdc ?? '', dsc:f.dsc, mtc:f.mtc ?? '',
  prc:f.prc ?? '', lcc:f.lcc ?? 'ALL', qty:f.qty ?? null, amt:f.amt ?? null,
  rte:f.rte ?? null, cof:f.cof ?? null, unc:f.unc ?? '', ssh:f.ssh, srf:f.srf ?? '', nts:f.nts ?? '', exi:f.exi ?? {},
});

const parseMonthlyMatrix = (wb:XLSX.WorkBook, sheet:string, datasetcode:string, valueField:'qty'|'amt', fy:string) => {
  const rows = rowsOf(wb, sheet);
  const headerIndex = rows.findIndex((r) => r.some((x:any) => norm(x)==='MOIS') && r.filter((x:any) => ['SUPER','SUPER 91','PETROLE','GASOIL','DDO','FUEL OIL','JET A1','GAZ ','TOTAL'].includes(norm(x))).length >= 3);
  if (headerIndex < 0) return [];
  const header = rows[headerIndex];
  const monthCols = header.map((x:any,i:number)=> norm(x)==='MOIS' ? i : -1).filter((i:number)=>i>=0);
  const facts:any[] = [];

  monthCols.forEach((start:number, bi:number) => {
    const end = bi+1 < monthCols.length ? monthCols[bi+1] : header.length;
    let loc = 'ALL';
    const title = String(rows[Math.max(0,headerIndex-3)]?.[start] ?? '').toUpperCase();
    if (title.includes('BINGO')) loc = 'BINGO';
    else if (title.includes('BOBO') || title.includes('B0BO')) loc = 'BOBO';
    else if (title.includes('DROITURE')) loc = 'DROITURES';

    for (let ri=headerIndex+1; ri<rows.length; ri++) {
      const r = rows[ri];
      const m = monthNo(r[start]);
      if (!m) continue;
      for (let c=start+1; c<end; c++) {
        const prc = code(header[c]);
        if (!prc || prc === 'TOTAL' || prc === 'MOIS') continue;
        const v = num(r[c]);
        if (v === null) continue;
        addFact(facts, {fyr:fy,mno:m,pdc:period(fy,m),dsc:datasetcode,mtc:datasetcode,prc,lcc:loc,[valueField]:v,unc:valueField==='qty'?'VOLUME':'FCFA',ssh:sheet,srf:`R${ri+1}C${c+1}`});
      }
    }
  });

  return facts;
};

export const workbookToRawSheets = (wb:XLSX.WorkBook) =>
  wb.SheetNames.map((sheetname) => {
    const rows = rowsOf(wb, sheetname);
    const colcount = rows.reduce((m,r)=>Math.max(m,r.length),0);
    return {sheetname, rowcount:rows.length, colcount, rows};
  });

export const determinantWorkbookToFacts = (wb:XLSX.WorkBook, fiscalYear:string) => {
  let facts:any[] = [];
  facts = facts.concat(parseMonthlyMatrix(wb,'PREV VTES PAR MOIS VOLUME 2025','SALES_VOLUME','qty',fiscalYear));
  facts = facts.concat(parseMonthlyMatrix(wb,'PREV VTES PAR MOIS VALEUR 2025','SALES_VALUE','amt',fiscalYear));
  facts = facts.concat(parseMonthlyMatrix(wb,'PREVISIONS ACHATS PAR MOIS VOL','PURCHASE_VOLUME','qty',fiscalYear));
  facts = facts.concat(parseMonthlyMatrix(wb,'PREVISIONS ACHATS PAR MOIS FCFA','PURCHASE_VALUE','amt',fiscalYear));

  rowsOf(wb,'COEFFICIENTS MENSUELS').forEach((r,ri,rows) => {
    if (norm(r[0])==='MOIS') {
      const header = r;
      for (let i=ri+1; i<rows.length; i++) {
        const rr = rows[i]; const m = monthNo(rr[0]); if (!m) continue;
        for (let c=1; c<header.length; c++) {
          const prc = code(header[c]); const cof = num(rr[c]);
          if (prc && prc !== 'TOTAL' && cof !== null) addFact(facts,{fyr:fiscalYear,mno:m,pdc:period(fiscalYear,m),dsc:'MONTH_COEFFICIENT',mtc:'MONTH_COEFFICIENT',prc,cof,unc:'COEF',ssh:'COEFFICIENTS MENSUELS',srf:`R${i+1}C${c+1}`});
        }
      }
    }
  });

  rowsOf(wb,'BRENT DOLLAR').forEach((r,ri) => {
    const m = monthNo(r[2]); if (!m) return;
    const brent = num(r[3]); const usd = num(r[4]);
    if (brent !== null) addFact(facts,{fyr:fiscalYear,mno:m,pdc:period(fiscalYear,m),dsc:'ECONOMIC_INDICATOR',mtc:'BRENT_USD',rte:brent,unc:'USD',ssh:'BRENT DOLLAR',srf:`R${ri+1}C4`});
    if (usd !== null) addFact(facts,{fyr:fiscalYear,mno:m,pdc:period(fiscalYear,m),dsc:'ECONOMIC_INDICATOR',mtc:'USD_XOF',rte:usd,unc:'XOF',ssh:'BRENT DOLLAR',srf:`R${ri+1}C5`});
  });

  rowsOf(wb,'RESULTAT PREVISIONNEL').forEach((r,ri) => {
    const label = String(r[0] ?? '').trim();
    const amt = num(r[1]);
    if (!label || label.toUpperCase()==='RUBRIQUES' || amt === null) return;
    addFact(facts,{fyr:fiscalYear,dsc:'FORECAST_RESULT',mtc:label.toUpperCase().replace(/[^A-Z0-9]+/g,'_').slice(0,80),amt,unc:'FCFA',ssh:'RESULTAT PREVISIONNEL',srf:`R${ri+1}`,nts:label});
  });

  return facts;
};

export const readWorkbookFile = async (file:File) => {
  const data = await file.arrayBuffer();
  return XLSX.read(data, {type:'array', cellDates:true});
};
